import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  useFetchOrdersData,
  useFetchOrderTemplate,
  useOrderBulkDocMutation,
  useFetchFmOrderTemplate,
  useFmOrderBulkDocMutation,
} from "../../../apis/orderApis";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
} from "../../../utilities/bulkUploadHelper";
import toast from "react-hot-toast";

type OrderBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene?: () => void;
  setOrderBulkResponse: React.Dispatch<React.SetStateAction<any>>;
};

export default function OrderBulkUploadModals({
  onClose,
  onResponseOpene,
  setOrderBulkResponse,
}: OrderBulkUploadModalsProps) {
  const [file, setFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [orderType, setOrderType] = useState<"LM" | "FM">("LM");

  const { mutate: uploadLMDocMutate, isPending: isLMBulkUploading } =
    useOrderBulkDocMutation();
  const { mutate: uploadFMDocMutate, isPending: isFMBulkUploading } =
    useFmOrderBulkDocMutation();

  const isBulkUploading = orderType === "LM" ? isLMBulkUploading : isFMBulkUploading;

  const { data: templateData, refetch: fetchTemplates } =
    useFetchOrderTemplate();
  const { data: fmTemplateData, refetch: fetchFmTemplates } =
    useFetchFmOrderTemplate();

  const {
    refetch,
  } = useFetchOrdersData(
    pageNo,
    limit,
    searchFor,
    activeTab === "Reverse",
    activeTab === "Cancelled",
    activeTab === "Deleted"
  );

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    const targetTemplateObj =
      orderType === "LM" ? templateData?.[0] : fmTemplateData?.[0];
    const cleanedFile = await cleanUploadedExcelFile(file, targetTemplateObj);

    const uploadMutate = orderType === "LM" ? uploadLMDocMutate : uploadFMDocMutate;
    uploadMutate(
      { orders: cleanedFile },
      {
        onSuccess: (data: any) => {
          setOrderBulkResponse(data.data);
          onClose();
          if (onResponseOpene) {
            onResponseOpene();
          }
          refetch();
          toast.success(`${orderType} Orders uploaded successfully`);
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      }
    );
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    if (orderType === "LM") {
      const worksheet = workbook.addWorksheet("Order Template");

      const infoRow = worksheet.addRow([
        "This template is for importing order data. Fields in red are mandatory; others are optional. Please do not modify the first 4 rows (Row 4 is sample/dummy data).",
      ]);
      worksheet.mergeCells(`A1:T1`);
      infoRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E2EFD9" },
        };
        cell.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "212121" },
        };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });

      infoRow.height = 25;

      const validationRow = worksheet.addRow([
        "Client Id is required",
        "Reference Number is required",
        "Full name is required",
        "Receiver Phone (10–12 digits) is required",
        "Receiver Email is Optional",
        "Receiver Country.",
        "Receiver City  is required",
        "Receiver District / Area", 
        "Receiver Address is required",
        "Receiver National Address  is required",
        "Shipment Cost is Optional", 
        "Payment Type (CC, COD)",
        "Cod Amount is required", 
        "Number of Pieces is required", 
        "Weight is required", 
        "Service is required",  
        "Shipment Type (PARCEL)",  
        "Description is Optional", 
        "Latitude is Optional" ,
        "Longitude is Optional"
      ]);
      validationRow.eachCell((cell) => {
        let fillColor = "FFCCCC";

        const optionalCols = [5, 11, 18, 19, 20];
        if (optionalCols.includes(Number(cell.col))) {
          fillColor = "FFEB9C";
        }
        
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };

        cell.font = { color: { argb: "212121" } };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      validationRow.height = 60;

      const headings = Object.keys(templateData?.[0] || {});
      const headingsRow = worksheet.addRow(headings);

      headingsRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "217447" },
        };
        cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      headingsRow.height = 25;

      // Row 4: Dummy Data Row
      let dummyRow: ExcelJS.Row | undefined;
      if (templateData && templateData.length > 0 && templateData[0]) {
        const dummyValues = Object.values(templateData[0]);
        dummyRow = worksheet.addRow(dummyValues);
        dummyRow.eachCell((cell: any) => {
          cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });
        dummyRow.height = 20;
      }

      worksheet.columns = Array(headings.length).fill({ width: 25 });
      await styleAndProtectDummyRow(worksheet, dummyRow);

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "order-template.xlsx");
      toast.success("LM Orders template downloaded successfully");
    } else {
      const worksheet = workbook.addWorksheet("FM Orders Template");

      const infoRow = worksheet.addRow([
        "FM Orders Template — One product per row. For multiple products in the same order, repeat the same Reference No & Client ID on separate rows with different Product IDs. Do NOT modify the first 4 rows (Row 4 is sample/dummy data).",
      ]);
      worksheet.mergeCells(`A1:S1`);
      infoRow.eachCell((cell: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E2EFD9" },
        };
        cell.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "212121" },
        };
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      infoRow.height = 30;

      const validationRow = worksheet.addRow([
        "Client ID is required",
        "Reference No is required",
        "Receiver Name is required",
        "Receiver Phone (9-12 digits) is required",
        "Receiver Email (optional)",
        "Receiver Country is required",
        "Receiver City (optional if National Address provided)",
        "Receiver District / Area (optional)",
        "Receiver Address (optional if National Address provided)",
        "National Address (optional if City+Address provided)",
        "Shipment Cost (optional)",
        "Payment Type: CC or COD ",
        "COD Amount (0 for CC, required if COD)",
        "No of Pieces is required (numeric ≥1)",
        "Service (optional)",
        "Description (optional)",
        "Latitude (optional)",
        "Longitude (optional)",
        "Product ID is required (SKU) — one per row",
        "Quantity is required (numeric ≥1)",
      ]);
      validationRow.eachCell((cell: any) => {
        let fillColor = "FFCCCC";

        const optionalCols = [5, 7, 8, 9, 10, 11, 13, 15, 16, 17, 18];
        if (optionalCols.includes(Number(cell.col))) {
          fillColor = "FFEB9C";
        }

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };

        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };

        cell.font = { color: { argb: "212121" }, size: 9 };

        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      validationRow.height = 80;

      const headings = [
          "client_id",
          "reference_no",
          "receiver_name",
          "receiver_mobile",
          "receiver_email",
          "receiver_country",
          "receiver_city",
          "receiver_district",
          "receiver_address",
          "receiver_national_address",
          "shipment_cost",
          "payment_type",
          "cod_amount",
          "no_of_pieces",
          "service",
          "description",
          "latitude",
          "longitude",
          "product_id",
          "quantity",
        ];

      const headersRow = worksheet.addRow(headings);

      headersRow.eachCell((cell: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "217447" },
        };
        cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      headersRow.height = 25;

      // Row 4: Dummy Data Row
      let dummyRow: ExcelJS.Row | undefined;
      if (fmTemplateData && fmTemplateData.length > 0 && fmTemplateData[0]) {
        const dummyValues = Object.values(fmTemplateData[0]);
        dummyRow = worksheet.addRow(dummyValues);
        dummyRow.eachCell((cell: any) => {
          cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });
        dummyRow.height = 20;
      }

      worksheet.columns = Array(headings.length).fill({ width: 22 });
      await styleAndProtectDummyRow(worksheet, dummyRow);
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "fm-orders-template.xlsx");
      toast.success("FM Orders template downloaded successfully");
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchFmTemplates();
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="rounded-2xl ">
        <div className="flex items-center">
          <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px] ">
            <h2 className="text-2xl">Bulk Upload {orderType} Orders</h2>
          </div>
          <div className="relative ">
            <button
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
          <div className="w-[655px] space-y-4">
            
            <div className="flex gap-4 p-2 bg-[#FCFDF8] border border-[#D2D2D2] rounded-md">
              <span className="text-sm font-semibold text-[#212121] mr-2">Order Type:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orderType"
                  value="LM"
                  checked={orderType === "LM"}
                  onChange={() => {
                    setOrderType("LM");
                    setFile(null);
                  }}
                  className="accent-[#FBD037]"
                />
                <span className="text-sm font-medium text-[#212121]">LM Orders</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orderType"
                  value="FM"
                  checked={orderType === "FM"}
                  onChange={() => {
                    setOrderType("FM");
                    setFile(null);
                  }}
                  className="accent-[#FBD037]"
                />
                <span className="text-sm font-medium text-[#212121]">FM Orders</span>
              </label>
            </div>

            <FileUploader
              name="photo"
              extraParentClasses="col-span-3"
              img="/img/upload-img.png"
              title={`Upload ${orderType} Excel/CSV File`}
              message="Upload an Excel or CSV file (Max size: 2 MB)." 
              classNameMain="bg-transparent border-none flex items-center gap-3"
              fileVal={file}
              setFileVal={(file: any) => {
                setFile(file);
              }}
              showPreview={true}
              required
            />

            {file && (
              <p className="text-sm text-green-600 font-medium">
                Selected File: {file.name}
              </p>
            )}

            <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
              <li>Download the Excel/CSV template before uploading.</li>
              <li>Do not change column headers.</li>
              {orderType === "FM" ? (
                <>
                  <li>Include product details (Product ID & Quantity) for each order.</li>
                  <li>Weight will be calculated from products - do not include weight column.</li>
                </>
              ) : (
                <li>Mandatory fields: Client ID, Receiver, Reference, Weight, etc.</li>
              )}
            </ul>

            <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
              <p className="text-xs text-[#212121] font-medium">
                <b>Note</b> : Please upload the file in the same format as the
                downloaded file.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm"
              >
                <img src="/img/csvxl.svg" alt="" />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px]">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading || !file}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isBulkUploading ? "Uploading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
